'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { matchesTeamMemberOperationsRealtime } from './teamMemberOperationsRealtime';
import { normalizeOperationsRealtimeMessage } from '@/lib/websocket/operationsRealtime';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export interface TeamMemberLiveLocationUpdate {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
  source: string | null;
  workItemId: string | null;
  updatedAt: string;
}

interface UseTeamMemberOperationsRealtimeOptions {
  teamMemberUserId?: number | null;
  relevantAppointmentIds?: Iterable<string | number | null | undefined>;
  enabled?: boolean;
  /** Gate live-location handling — false for members whose location is
   * not visible (non-field workers; is_field_worker is the single flag). */
  locationUpdatesEnabled?: boolean;
  onRelevantMessage: () => void;
  onLocationUpdate?: (location: TeamMemberLiveLocationUpdate) => void;
}

// Events carrying a worker GPS fix on the operations channel.
const LIVE_LOCATION_EVENTS = new Set([
  'worker:location_update',
  'location:updated',
]);

function toFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toTimestampIso(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    // WS `location_update` payloads carry epoch milliseconds.
    const date = new Date(value > 1e12 ? value : value * 1000);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  return new Date().toISOString();
}

function extractLocationUpdate(
  payload: UnknownRecord,
  fallbackUserId: unknown,
  fallbackTimestamp: unknown,
): { userId: number; location: TeamMemberLiveLocationUpdate } | null {
  const userId = toFiniteNumber(payload.user_id ?? fallbackUserId);
  const latitude = toFiniteNumber(payload.latitude ?? payload.lat);
  const longitude = toFiniteNumber(payload.longitude ?? payload.lng);

  if (userId == null || latitude == null || longitude == null) {
    return null;
  }

  return {
    userId,
    location: {
      latitude,
      longitude,
      accuracy: toFiniteNumber(payload.accuracy),
      heading: toFiniteNumber(payload.heading ?? payload.bearing),
      speed: toFiniteNumber(payload.speed),
      source: typeof payload.source === 'string' && payload.source.trim() ? payload.source : null,
      workItemId: payload.work_item_id != null && String(payload.work_item_id).trim()
        ? String(payload.work_item_id)
        : null,
      updatedAt: toTimestampIso(
        payload.last_updated_at ?? payload.timestamp ?? fallbackTimestamp,
      ),
    },
  };
}

export function useTeamMemberOperationsRealtime({
  teamMemberUserId,
  relevantAppointmentIds,
  enabled = true,
  locationUpdatesEnabled = true,
  onRelevantMessage,
  onLocationUpdate,
}: UseTeamMemberOperationsRealtimeOptions): void {
  const { client } = useAuth();
  // Session active client (JWT client_id claim — same value
  // authenticateAndGetClient resolves on API calls). user.client_id is 0
  // for root/multi-client users.
  const clientId = authService.getSessionClientId() ?? Number(client?.id);
  const isEnabled = enabled && Number.isFinite(teamMemberUserId) && Number(teamMemberUserId) > 0;
  const locationEnabled = isEnabled && locationUpdatesEnabled;

  // Refs keep the WS callbacks stable so the subscriptions don't churn on
  // every render when callers pass inline lambdas.
  const onLocationUpdateRef = useRef(onLocationUpdate);
  const onRelevantMessageRef = useRef(onRelevantMessage);

  useEffect(() => {
    onLocationUpdateRef.current = onLocationUpdate;
    onRelevantMessageRef.current = onRelevantMessage;
  });

  const operationsChannel = useMemo(() => (
    Number.isFinite(clientId) && clientId > 0
      ? `operations:client:${clientId}`
      : undefined
  ), [clientId]);

  const realtimeTaskChannel = useMemo(() => (
    Number.isFinite(clientId) && clientId > 0 && Number.isFinite(teamMemberUserId) && Number(teamMemberUserId) > 0
      ? `tasks:user:${Number(teamMemberUserId)}:client:${clientId}`
      : undefined
  ), [clientId, teamMemberUserId]);

  // Field worker GPS fixes are broadcast on location:client:{id} by the Go
  // websocket service (Redis client:{id}:tech_locations).
  const locationChannel = useMemo(() => (
    Number.isFinite(clientId) && clientId > 0
      ? `location:client:${clientId}`
      : undefined
  ), [clientId]);

  const emitLocationIfMatch = useCallback((payload: UnknownRecord, fallbackUserId: unknown, fallbackTimestamp: unknown): boolean => {
    const extracted = extractLocationUpdate(payload, fallbackUserId, fallbackTimestamp);
    if (!extracted || extracted.userId !== Number(teamMemberUserId)) {
      return false;
    }

    onLocationUpdateRef.current?.(extracted.location);
    return true;
  }, [teamMemberUserId]);

  const handleRealtimeMessage = useCallback((data: unknown) => {
    if (!isEnabled) {
      return;
    }

    // Location pushes are not refresh events — handle them first so they can
    // update the live marker without forcing a full profile re-fetch.
    const normalized = normalizeOperationsRealtimeMessage(data);
    if (locationEnabled && normalized && LIVE_LOCATION_EVENTS.has(normalized.event)) {
      const payload = isRecord(normalized.payload) ? normalized.payload : {};
      if (emitLocationIfMatch(payload, normalized.actor_user_id, normalized.timestamp)) {
        // worker:location_update is emitted alongside workflow transitions, so
        // a profile refresh is still warranted for the rest of the snapshot.
        onRelevantMessageRef.current();
      }
      return;
    }

    const match = matchesTeamMemberOperationsRealtime({
      data,
      teamMemberUserId,
      relevantAppointmentIds,
    });
    if (!match) {
      return;
    }

    if (!match.matchesUser && !match.matchesAppointment) {
      return;
    }

    onRelevantMessageRef.current();
  }, [isEnabled, locationEnabled, relevantAppointmentIds, teamMemberUserId, emitLocationIfMatch]);

  const handleLocationChannelMessage = useCallback((data: unknown) => {
    if (!locationEnabled || !isRecord(data)) {
      return;
    }

    // Raw payload published by the Go location handler:
    // { type: 'field_worker_location', user_id, latitude, longitude, ... }
    emitLocationIfMatch(data, data.user_id, data.timestamp);
  }, [locationEnabled, emitLocationIfMatch]);

  useWebSocketChannel({
    channel: isEnabled ? operationsChannel : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: handleRealtimeMessage,
  });

  useWebSocketChannel({
    channel: isEnabled ? realtimeTaskChannel : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: handleRealtimeMessage,
  });

  useWebSocketChannel({
    channel: locationEnabled && onLocationUpdate ? locationChannel : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: handleLocationChannelMessage,
  });
}
